import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { Subject } from 'rxjs';
import { IndicatorState } from '../../sb-pending-button-indicator/sb-pending-button-indicator.component';
import {
    debounceTime,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { SbCompanyLogoComponent } from '../../sb-company-logo/sb-company-logo.component';
import { TestSelectorDirective } from '../../../directives/test-selector.directive';
import { RouterLink } from '@angular/router';
import { InstrumentsQuery } from '../../../state/instruments/instruments.query';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { Company, Instrument } from '../../../state/instruments/instrument.model';
import { CountryCodePipe } from '../../../pipes/country-code.pipe';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';

@Component({
    selector: 'sb-private-equity-company-select',
    standalone: true,
    imports: [
        CommonModule,
        MatSelectModule,
        MatInputModule,
        SbCompanyLogoComponent,
        ReactiveFormsModule,
        TestSelectorDirective,
        RouterLink,
        CountryCodePipe,
        MatTooltipModule
    ],
    templateUrl: './sb-private-equity-company-select.component.html',
    styleUrls: ['./sb-private-equity-company-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SbPrivateEquityCompanySelectComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: SbPrivateEquityCompanySelectComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbPrivateEquityCompanySelectComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    _selectedInstrumentId: number;
    get sbTestSelector(): string {
        return this._sbTestSelector;
    }
    get appearance(): string {
        return this._appearance;
    }

    @Input() set appearance(value: string) {
        this._appearance = value;
    }
    get touched(): boolean {
        return this._touched;
    }

    @Input() set sbTestSelector(value: string) {
        this._sbTestSelector = value;
    }

    @Input() set touched(value: boolean) {
        if (value) {
            this.markAsTouched();
            this.companyCtrl.markAsTouched();
        } else {
            this._touched = value;
            this.companyCtrl.markAsUntouched();
        }
    }

    @Input() set selectedInstrumentId(id: number) {
        if (id) {
            this.instrumentsQuery
                .selectInstrumentById$(id)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((instrument) => {
                    if (instrument) {
                        this.writeValue(instrument);
                    }
                });
            this._selectedInstrumentId = id;
        }
    }

    constructor(
        private _cdr: ChangeDetectorRef,
        private instrumentsQuery: InstrumentsQuery
    ) {}

    unsubscribe$ = new Subject();
    private _appearance: string = 'fill';
    companyCtrl = new FormControl<Instrument<Company>>(null);
    state: IndicatorState = null;

    onChange = (company) => {};

    onTouched = () => {};

    private _touched = false;

    disabled = false;

    writeValue(company) {
        this.companyCtrl.patchValue(company);
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this._touched = true;
        }
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // inherit errors from control value accessor
        setTimeout(() => this.companyCtrl.setErrors(control.errors));
        // no custom validators implemented
        return null;
    }

    companySearch = new FormControl('');
    companies$ = this.companySearch.valueChanges.pipe(
        startWith(''),
        filter((qs) => qs !== null),
        debounceTime(300),
        switchMap((qs) => {
            let _filters: Record<string, any> = {
                decoratedInstrumentName: { qs },
                type: { eq: 'COMPANY' },
                deleted: { eq: false }
            };
            if (this._selectedInstrumentId) {
                _filters = {
                    ..._filters,
                    instrumentId: { eq: Number(this._selectedInstrumentId) }
                };
            }

            return this.instrumentsQuery
                .selectInstrumentsByFilter$<Company>(_filters)
                .pipe(
                    map((instruments) => {
                        return instruments
                            .filter((instrument) => {
                                const name =
                                    instrument.decoratedInstrumentName || instrument.name;
                                if (name && typeof qs === 'string') {
                                    return (
                                        name.toLowerCase().indexOf(qs.toLowerCase()) > -1
                                    );
                                } else {
                                    console.error(
                                        instrument,
                                        ' does not have a string name or decoratedInstrumentName.'
                                    );
                                    return false;
                                }
                            })
                            .sort((a, b) => {
                                const nameA = a.decoratedInstrumentName || a.name;
                                const nameB = b.decoratedInstrumentName || b.name;
                                return nameA.localeCompare(nameB);
                            });
                    })
                );
        }),
        shareReplay(1)
    );

    compareCompanyFn = (a: Instrument<Company>, b: Instrument<Company>) => {
        return a && b ? a.instrumentId === b.instrumentId : a === b;
    };

    private _sbTestSelector: string = 'private-equity-company-select';

    ngOnInit(): void {
        this.companyCtrl.valueChanges
            .pipe(
                tap((value) => {
                    this.markAsTouched();
                    this.onChange(value);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // Autofill company on component init if only one found for selected ID
        this.companies$.pipe(take(1)).subscribe((companies) => {
            if (companies.length === 1) {
                this.writeValue(companies[0]);
                this.disabled = true;
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
